'use client';

import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';

import { DetailTopBarIcon } from '@/components/DetailTopBarIconButton';
import { cn } from '@/lib/cn';
import { resolveAppHref } from '@/lib/portalRoutes';
import { useIsDesktopApp } from '@/hooks/useIsDesktopApp';

import { Breadcrumb } from './Breadcrumb';
import { PageActionsSlot, PageRefreshSlot, PageUsecaseSlot, useMobileDetailTopBarConfig } from './PageActionsSlot';

type Translate = (key: string) => string;

type TopBarProps = {
  drawerId: string;
  isDrawerOpen: boolean;
  tLayout: Translate;
  onOpenSearch?: () => void;
  searchOnlyOnMobile?: boolean;
  desktopSearchTrigger?: ReactNode;
  notificationBell?: ReactNode;
  userMenu?: ReactNode;
  hideMenuToggle?: boolean;
  showLogo?: boolean;
  logoIconOnly?: boolean;
  showHomeButton?: boolean;
  headerExtras?: ReactNode;
};

export const TopBar = ({
  drawerId,
  isDrawerOpen,
  tLayout,
  onOpenSearch,
  searchOnlyOnMobile = false,
  desktopSearchTrigger,
  notificationBell,
  userMenu,
  hideMenuToggle = false,
  showLogo = false,
  logoIconOnly = false,
  showHomeButton = false,
  headerExtras,
}: TopBarProps) => {
  const mobileDetailTopBar = useMobileDetailTopBarConfig();
  const mobileDetailActions = mobileDetailTopBar?.actions ?? [];
  // 데스크탑 앱: 프로젝트 탭이 홈으로 이탈하지 않도록 모바일 홈 버튼 제거.
  const isDesktopApp = useIsDesktopApp();

  return (
    <header className="sticky top-0 z-30 shrink-0 border-b border-[var(--color-border)] bg-base-100 lg:static">
      {mobileDetailTopBar ? (
        <div className="flex h-14 items-center gap-2 px-3 lg:hidden">
          <Link
            href={mobileDetailTopBar.backHref}
            aria-label="목록으로 돌아가기"
            className="btn btn-square btn-ghost btn-sm shrink-0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="min-w-0 flex-1 text-left text-base font-semibold text-base-content">
            <span className="block truncate">{mobileDetailTopBar.title}</span>
          </div>
          {mobileDetailActions.length > 0 ? (
            <div className="dropdown dropdown-end shrink-0">
              <button type="button" tabIndex={0} className="btn btn-square btn-ghost btn-sm" aria-label="상세 작업">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="5" r="1.8" />
                  <circle cx="12" cy="12" r="1.8" />
                  <circle cx="12" cy="19" r="1.8" />
                </svg>
              </button>
              <ul
                tabIndex={0}
                className="menu dropdown-content z-[1] mt-1 w-44 rounded-box border border-base-300 bg-base-100 p-1 shadow"
              >
                {mobileDetailActions.map((action) => (
                  <li key={action.key}>
                    <button
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={cn(
                        'flex items-center gap-2',
                        action.tone === 'danger' ? 'text-error' : null,
                        action.tone === 'success' ? 'text-success' : null,
                      )}
                    >
                      <DetailTopBarIcon icon={action.icon} />
                      <span>{action.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="h-8 w-8 shrink-0" aria-hidden="true" />
          )}
        </div>
      ) : null}
      <div
        className={cn(
          'h-14 items-center gap-2 px-3 md:gap-3 md:px-6 lg:px-8',
          mobileDetailTopBar ? 'hidden lg:flex' : 'flex',
        )}
      >
        {hideMenuToggle ? null : (
          <label
            htmlFor={drawerId}
            aria-label={tLayout('openSidebar')}
            aria-controls={drawerId}
            aria-expanded={isDrawerOpen}
            role="button"
            tabIndex={0}
            className="btn btn-square btn-ghost btn-sm shrink-0 lg:hidden"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              className="inline-block h-5 w-5 stroke-current"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </label>
        )}
        {showLogo ? (
          <Link
            href={resolveAppHref('/home')}
            aria-label="AgentTeams home"
            className="flex shrink-0 items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-base-200"
          >
            <Image
              src="/sidebar-logo.svg"
              alt=""
              width={24}
              height={24}
              className="h-6 w-6 rounded-md shadow-xs"
              priority
            />
            {logoIconOnly ? null : (
              <>
                <span className="text-sm font-bold tracking-tight">AgentTeams</span>
                <span className="rounded px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-warning/20 text-yellow-800 dark:bg-warning/25 dark:text-warning">
                  Beta
                </span>
              </>
            )}
          </Link>
        ) : null}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="min-w-0">
            <Breadcrumb />
          </div>
          <div className="flex shrink-0 items-center">
            <PageRefreshSlot className="flex shrink-0 items-center" />
            <PageUsecaseSlot className="flex shrink-0 items-center" />
          </div>
        </div>
        {desktopSearchTrigger ? (
          <div className="hidden w-56 shrink-0 xl:w-64 lg:block">{desktopSearchTrigger}</div>
        ) : null}
        {onOpenSearch ? (
          <button
            type="button"
            aria-label={tLayout('commandPaletteTriggerLabel')}
            onClick={onOpenSearch}
            className={`btn btn-square btn-ghost btn-sm shrink-0${searchOnlyOnMobile ? ' lg:hidden' : ''}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        ) : null}
        {headerExtras}
        <PageActionsSlot className="flex shrink-0 items-center gap-1.5" />
        {notificationBell}
        {showHomeButton && !isDesktopApp ? (
          <Link
            href={resolveAppHref('/home')}
            aria-label={tLayout('goHome')}
            className="btn btn-square btn-ghost btn-sm shrink-0 lg:hidden"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="h-5 w-5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </Link>
        ) : null}
        {userMenu}
      </div>
    </header>
  );
};

export { BreadcrumbProvider, useBreadcrumb } from './Breadcrumb';
export type { BreadcrumbItem } from './Breadcrumb';
export { PageActionsSlotProvider, HideableTopBar, useHideTopBar } from './PageActionsSlot';
export { usePageActions } from './usePageActions';
export { useMobileDetailTopBar } from './useMobileDetailTopBar';
export { usePageRefresh } from './usePageRefresh';
export { useUsecaseLink } from './useUsecaseLink';
